import { useState } from 'react'
import { useForm, usePage, router, Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageProps } from '@/types'
import { UserPlus, Send, RefreshCw, Clock, CheckCircle, XCircle, ArrowRight, Users } from 'lucide-react'

interface Referral {
  id: number
  first_name: string
  last_name: string
  email: string
  status: 'invited' | 'document_signed' | 'past_agent'
  document_sent_at: string | null
  document_signed_at: string | null
  docuseal_submission_id: string | null
  created_at: string
  converted_agent_id: number | null
}

interface Props extends PageProps {
  referrals: Referral[]
  default_template_id: string | null
  can_send_referrals: boolean
  can_view_team: boolean
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
    invited: {
      label: 'Invited',
      className: 'bg-amber-100 text-amber-800',
      icon: <Clock className="h-3.5 w-3.5" />
    },
    document_signed: {
      label: 'Document Signed',
      className: 'bg-green-100 text-green-800',
      icon: <CheckCircle className="h-3.5 w-3.5" />
    },
    past_agent: {
      label: 'Past Agent',
      className: 'bg-gray-100 text-gray-600',
      icon: <XCircle className="h-3.5 w-3.5" />
    }
  }

  const { label, className, icon } = config[status] || config.invited

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${className}`}>
      {icon}
      {label}
    </span>
  )
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

export default function ReferralsIndex() {
  const page = usePage<Props>().props
  const referrals = page.referrals ?? []
  const can_send_referrals = page.can_send_referrals ?? false
  const can_view_team = page.can_view_team ?? false
  const [showModal, setShowModal] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [resendingId, setResendingId] = useState<number | null>(null)

  const form = useForm({
    referral: {
      first_name: '',
      last_name: '',
      email: ''
    },
    send_agreement: 'false'
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setShowModal(false)
    setShowConfirmation(true)
  }

  const confirmAndSend = (sendAgreement: boolean) => {
    setShowConfirmation(false)

    form.transform((data) => ({
      ...data,
      send_agreement: sendAgreement ? 'true' : 'false'
    }))

    form.post('/referrals', {
      onSuccess: () => {
        form.reset()
      }
    })
  }

  const fieldErrors = form.errors as Record<string, string>

  const handleResend = (referralId: number) => {
    setResendingId(referralId)
    router.post(`/referrals/${referralId}/resend_document`, {}, {
      preserveScroll: true,
      onFinish: () => setResendingId(null)
    })
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-heading font-bold text-chestnut-charcoal">My Referrals</h1>
            <p className="text-sm text-chestnut-charcoal/60 mt-1">
              Refer new agents and track their onboarding status.
            </p>
          </div>
          <div className="flex items-center gap-4">
            {can_view_team && (
              <Link
                href="/my/team"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-chestnut-forest hover:text-chestnut-forest/80"
              >
                View My Team
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            <button
              onClick={() => setShowModal(true)}
              disabled={!can_send_referrals}
              title={can_send_referrals ? '' : 'Referrals are disabled for your account. Contact an administrator.'}
              className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus className="h-4 w-4" />
              Refer an Agent
            </button>
          </div>
        </div>

        {!can_send_referrals && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Sending new referrals is currently disabled for your account. Please contact an administrator if you believe this is a mistake.
          </div>
        )}

        {referrals.length === 0 ? (
          <div className="bg-white rounded-xl border border-chestnut-gray p-12 text-center">
            <UserPlus className="h-12 w-12 text-chestnut-charcoal/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-chestnut-charcoal mb-2">No referrals yet</h3>
            <p className="text-chestnut-charcoal/60 mb-6">
              Start growing your team by referring new agents.
            </p>
            <button
              onClick={() => setShowModal(true)}
              disabled={!can_send_referrals}
              title={can_send_referrals ? '' : 'Referrals are disabled for your account. Contact an administrator.'}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus className="h-4 w-4" />
              Refer Your First Agent
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-chestnut-gray overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-chestnut-gray bg-chestnut-cream/50">
                    <th className="text-left px-4 py-3 text-xs font-medium text-chestnut-charcoal/60 uppercase tracking-wider">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-chestnut-charcoal/60 uppercase tracking-wider">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-chestnut-charcoal/60 uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-chestnut-charcoal/60 uppercase tracking-wider">Document Sent</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-chestnut-charcoal/60 uppercase tracking-wider">Document Signed</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-chestnut-charcoal/60 uppercase tracking-wider">Referred On</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-chestnut-charcoal/60 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-chestnut-gray">
                  {referrals.map((referral) => (
                    <tr
                      key={referral.id}
                      className={`hover:bg-chestnut-cream/30 ${referral.converted_agent_id ? 'bg-chestnut-cream/20' : ''}`}
                    >
                      <td className={`px-4 py-3 text-sm font-medium ${referral.converted_agent_id ? 'text-chestnut-charcoal/60' : 'text-chestnut-charcoal'}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{referral.first_name} {referral.last_name}</span>
                          {referral.converted_agent_id && (
                            <Link
                              href={`/my/team#agent-${referral.converted_agent_id}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-chestnut-forest/10 text-chestnut-forest hover:bg-chestnut-forest/15"
                              title="View on My Team"
                            >
                              <Users className="h-3 w-3" />
                              Now on your team
                            </Link>
                          )}
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-sm ${referral.converted_agent_id ? 'text-chestnut-charcoal/50' : 'text-chestnut-charcoal/80'}`}>
                        {referral.email}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={referral.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-chestnut-charcoal/80">
                        {formatDate(referral.document_sent_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-chestnut-charcoal/80">
                        {formatDate(referral.document_signed_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-chestnut-charcoal/80">
                        {formatDate(referral.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {referral.status === 'invited' && (
                          <button
                            onClick={() => handleResend(referral.id)}
                            disabled={resendingId === referral.id || !can_send_referrals}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-chestnut-gold text-chestnut-gold hover:bg-chestnut-gold/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={!can_send_referrals ? 'Referrals are disabled for your account. Contact an administrator.' : (referral.document_sent_at ? 'Resend the agreement document' : 'Send the agreement document')}
                          >
                            {resendingId === referral.id ? (
                              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Send className="h-3.5 w-3.5" />
                            )}
                            {referral.document_sent_at ? 'Resend' : 'Send'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-chestnut-gray">
              <h2 className="text-lg font-heading font-bold text-chestnut-charcoal">Refer an Agent</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-chestnut-charcoal/40 hover:text-chestnut-charcoal"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.data.referral.first_name}
                  onChange={(e) => form.setData('referral', { ...form.data.referral, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-chestnut-gray rounded-lg focus:ring-2 focus:ring-chestnut-forest/20 focus:border-chestnut-forest outline-none"
                  placeholder="Enter first name"
                  required
                />
                {fieldErrors.first_name && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.first_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.data.referral.last_name}
                  onChange={(e) => form.setData('referral', { ...form.data.referral, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-chestnut-gray rounded-lg focus:ring-2 focus:ring-chestnut-forest/20 focus:border-chestnut-forest outline-none"
                  placeholder="Enter last name"
                  required
                />
                {fieldErrors.last_name && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.last_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.data.referral.email}
                  onChange={(e) => form.setData('referral', { ...form.data.referral, email: e.target.value })}
                  className="w-full px-3 py-2 border border-chestnut-gray rounded-lg focus:ring-2 focus:ring-chestnut-forest/20 focus:border-chestnut-forest outline-none"
                  placeholder="agent@example.com"
                  required
                />
                {fieldErrors.email && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-chestnut-charcoal bg-white border border-chestnut-gray rounded-lg hover:bg-chestnut-cream transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={form.processing}
                  className="btn-primary"
                >
                  Continue
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-chestnut-gray">
              <h2 className="text-lg font-heading font-bold text-chestnut-charcoal">Send User Agreement?</h2>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-chestnut-charcoal/80">
                Would you like to send the user agreement to{' '}
                <strong>{form.data.referral.first_name} {form.data.referral.last_name}</strong>{' '}
                at <strong>{form.data.referral.email}</strong> for signing?
              </p>

              <div className="bg-chestnut-cream/50 rounded-lg p-4 text-sm text-chestnut-charcoal/70">
                The agreement document will be sent via email. You can always resend it later if needed.
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => confirmAndSend(false)}
                  disabled={form.processing}
                  className="px-4 py-2 text-sm font-medium text-chestnut-charcoal bg-white border border-chestnut-gray rounded-lg hover:bg-chestnut-cream transition-colors"
                >
                  Skip for Now
                </button>
                <button
                  type="button"
                  onClick={() => confirmAndSend(true)}
                  disabled={form.processing}
                  className="btn-primary flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  {form.processing ? 'Sending...' : 'Yes, Send Agreement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
